import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftKind, DraftStatus, MountedDraftKind } from "@steelbill/types";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import { endpoints } from "@/lib/endpoints";
import type { EditorLine } from "@/features/invoices/use-invoice-editor";

/**
 * Autosaving an unfinished DOCUMENT to the server (§7.2 amendment, 2026-08-15).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EMERGENCY-LEAVE CASE. The owner is halfway through a 12-line bill when
 * the workshop calls. Before this hook the editor's state lived in component
 * memory: closing the tab lost every line. Now the form persists itself —
 * debounced, versioned, to a table that is deliberately NOT `invoices` or
 * `quotations` (a draft has no number and costs nothing to abandon; see the API
 * route's note).
 *
 * ONE HOOK, BOTH KINDS (second pass — the owner asked for quotations to be
 * saveable the way bills are). This file was `use-invoice-draft.ts` and was
 * invoice-shaped throughout. Copying it for quotations would have produced two
 * files that agree today and disagree the first time either is fixed — and the
 * two things they would disagree about are the version handshake and the "do
 * not autosave over a draft that has not loaded" guard, i.e. exactly the two
 * places where a mistake destroys typed work. So `kind` is a parameter, the
 * payload is a type parameter, and the API mount is chosen from {@link DRAFT_BASE}.
 *
 * GENERIC OVER THE PAYLOAD, NOT CAST TO ONE. The two editors hold genuinely
 * different states — an invoice's S.O./challan references versus a quotation's
 * subject, validity and §9.3 tax-display mode — and the server stores whatever
 * it is given, verbatim, without reading it. A cast would let the quotation
 * editor resume an invoice draft's payload and only fail at render.
 *
 * DEBOUNCED AT 2s, AND ONLY WHEN SOMETHING IS WORTH KEEPING. Every keystroke
 * would otherwise be a write; an empty form would save noise. "Worth keeping"
 * is a picked customer or a line with any text on it — see {@link worthKeeping},
 * which needs only the two fields both editor states share.
 *
 * THE VERSION RIDES EVERY SAVE. Two devices can hold the same draft; the API
 * refuses a save whose version is stale (409) and returns the winning state.
 * This hook surfaces that as `conflict` — the screen decides what to tell him.
 * A silent overwrite was rejected in design: losing typed lines silently is
 * the worst failure a form can have.
 *
 * READY IS ON THE FORM, NOT ON THE DOCUMENT. `status` here is DRAFT | READY and
 * belongs to `document_drafts`. Invoices remain DRAFT/ISSUED/CANCELLED and
 * quotations remain DRAFT/SENT/APPROVED/… — a drafts list needs to tell a
 * half-typed form from one that was checked and left for someone to issue, and
 * issuing is the irreversible act that spends a number (§6).
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Which mount a kind talks to. Both tables are keyed by `MountedDraftKind`, NOT
 * `DraftKind`, and the distinction has already paid for itself once: `CHALLAN`
 * was a legal value of the DB column with no editor and no routes for as long as
 * §17.7 was unbuilt, and keying by the wider type would have meant either a
 * build failure for an unwritten feature or a `Partial<Record<…>>` posting to
 * `undefined` at runtime. Widening the alias when §17.7 landed broke exactly
 * these two tables, which is where adding the endpoints is cheap.
 *
 * The paths themselves stay spelled once, in `lib/endpoints.ts` — these only
 * choose between them.
 */
const DRAFT_BASE: Record<MountedDraftKind, string> = {
  INVOICE: endpoints.invoiceDrafts,
  QUOTATION: endpoints.quotationDrafts,
  CHALLAN: endpoints.challanDrafts,
};

const DRAFT_ONE: Record<MountedDraftKind, (id: number | string) => string> = {
  INVOICE: endpoints.invoiceDraft,
  QUOTATION: endpoints.quotationDraft,
  CHALLAN: endpoints.challanDraft,
};

/**
 * Cache keys are `["drafts", kind, …]` — kind SECOND, so one kind's list can be
 * invalidated without touching the other's. Ids are globally unique across both
 * kinds (one table, one sequence), but keying a row without its kind would let a
 * quotation draft's response be served to the bill editor from cache.
 */
const draftsKey = (kind: DraftKind) => ["drafts", kind] as const;
const draftKey = (kind: DraftKind, id: string | number) => ["drafts", kind, String(id)] as const;

/** A row of the drafts list — `payload` is deliberately not on it (see the API). */
export interface DraftSummary {
  id: number;
  kind: DraftKind;
  /** DRAFT | READY, and it is the FORM's status. Never a document status. */
  status: DraftStatus;
  customerName: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** One draft, payload and all — what the editor resumes from. */
export interface DraftDetail<P> extends DraftSummary {
  payload: P;
}

/**
 * The two fields every draftable editor state has, and the only two this file
 * reads. Everything else in the payload is the editor's business.
 */
export interface DraftablePayload {
  customerId: number | null;
  lines: readonly EditorLine[];
}

/** The drafts list of one kind — newest touched first. */
export function useDrafts(kind: MountedDraftKind, enabled = true) {
  return useQuery({
    queryKey: draftsKey(kind),
    enabled,
    queryFn: async () => api.get<{ data: DraftSummary[] }>(DRAFT_BASE[kind]),
    select: (envelope) => envelope.data,
  });
}

/** One draft, payload and all — the editor resumes from this. */
export function useDraft<P>(kind: MountedDraftKind, id: string | undefined) {
  return useQuery({
    queryKey: draftKey(kind, id ?? ""),
    enabled: id !== undefined,
    queryFn: async () => api.get<{ data: DraftDetail<P> }>(DRAFT_ONE[kind](id ?? "")),
    select: (envelope) => envelope.data,
  });
}

/** Is there anything here worth a row on the server? */
function worthKeeping(state: DraftablePayload): boolean {
  if (state.customerId !== null) return true;
  return state.lines.some((line) => line.description.trim() !== "" || line.rate.trim() !== "");
}

export function useDraftAutosave<P extends DraftablePayload>({
  kind,
  state,
  customerName,
  draftId,
  initialVersion,
  initialStatus = "DRAFT",
  loaded = true,
  enabled,
}: {
  /** Decides the mount, the cache key and nothing else — there is no branch below. */
  kind: MountedDraftKind;
  state: P;
  customerName: string;
  /** Present when the editor was opened FROM a draft; otherwise created on first save. */
  draftId: number | null;
  initialVersion: number;
  /** The resumed draft's own status; a new draft always starts DRAFT server-side. */
  initialStatus?: DraftStatus;
  /**
   * Has the resumed GET settled? Only meaningful when `draftId` is set.
   *
   * The caller passes `resumed.data !== undefined`. Without it this hook cannot
   * distinguish "version 1 because it is a new draft" from "version 1 because
   * the real one has not arrived yet", and adopting the placeholder as though
   * it were the truth is how a resumed draft 409s against itself on its first
   * autosave. Defaults true so a brand-new draft needs no ceremony.
   */
  loaded?: boolean;
  /** Off in record mode — a hand-written bill already exists on paper. */
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const base = DRAFT_BASE[kind];
  const one = DRAFT_ONE[kind];

  const [id, setId] = useState<number | null>(draftId);
  /*
   * THE SAME ID, AS A REF, AND `persist` READS THIS ONE.
   *
   * `persist` is a `useCallback` and its `finally` drains a queued snapshot by
   * CALLING ITSELF. That recursive call runs the closure that is already on the
   * stack, which captured `id` as it was when the callback was created — so
   * after the very first save, when `id` was still null, the drained snapshot
   * POSTed a SECOND draft row instead of PUTting to the row just created:
   *
   *   /invoices/new, type a line. The 2s debounce POSTs; a workshop connection
   *   keeps it in flight. Typing continues, the next debounce queues a
   *   snapshot, the POST returns id 41 and `setId(41)` schedules a re-render —
   *   but `finally` recurses IMMEDIATELY, inside the old closure where
   *   `id === null`, and POSTs again. Two drafts for one bill, and the owner's
   *   later edits land on whichever one the editor happens to hold.
   *
   * A ref is read at call time rather than captured, so the recursion sees the
   * id the previous call just stored. `setId` stays for the render path.
   */
  const idRef = useRef<number | null>(draftId);
  const versionRef = useRef(initialVersion);

  /*
   * The resumed draft's version arrives AFTER mount (it rides the GET), and a
   * ref keeps its initial value — so adopt the loaded version once. Without
   * this every resumed draft's first autosave carried version 1 and 409'd
   * against itself.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * ADOPTED EXACTLY ONCE, MIRRORING THE EDITORS' `restored` GUARD ON THE
   * PAYLOAD. This used to key on `!savedOnce`, which let EVERY settle of the
   * resumed query overwrite the counter — and that silently destroyed work:
   *
   *   Draft 7 is at version 4. The son opens it on the phone, reads it, and
   *   locks the screen without typing, so `savedOnce` stays false. Dad edits
   *   draft 7 on the desktop; the server moves to version 7. The son's phone
   *   wakes, react-query refetches on window focus, and `initialVersion`
   *   arrives as 7 — which this effect adopts. The son now types one character.
   *   His autosave sends the version-4 PAYLOAD stamped version 7, the server
   *   sees a current version and accepts it, and dad's three edits are gone
   *   with NO 409 — the exact collision `version` exists to catch.
   *
   * The payload is adopted once (`restored` in each editor) so the version must
   * be too, or the two describe different moments in time. After the first
   * adoption the ref is owned by the save loop, which sets it from each
   * response.
   * ───────────────────────────────────────────────────────────────────────────
   */
  const versionAdopted = useRef(false);
  useEffect(() => {
    if (versionAdopted.current) return;
    // `initialVersion` defaults to 1 before the GET settles; adopting that is
    // harmless and correct for a brand-new draft, but it must not count as THE
    // adoption for a resumed one, or the real version never lands.
    if (draftId !== null && !loaded) return;
    versionAdopted.current = true;
    versionRef.current = initialVersion;
  }, [initialVersion, draftId, loaded]);

  /*
   * The status arrives the same way — on the resumed GET, after mount — and is
   * adopted the same way, until somebody presses the button.
   *
   * IT IS NOT ADOPTED FROM A SAVE'S RESPONSE, although every response carries
   * it. The save cannot CHANGE the status (the route refuses the field), so the
   * only thing reading it back could do is undo a press: a PUT that left before
   * the READY commit, and lands after it, answers with the row as it was and
   * would pull the badge back to DRAFT while the server holds READY. The
   * autosave fires every two seconds, so that overlap is not hypothetical.
   * Another device's flip therefore shows up here on reload rather than
   * instantly, which is the cheap half of the trade.
   */
  const [status, setStatus] = useState<DraftStatus>(initialStatus);
  const statusPressed = useRef(false);
  useEffect(() => {
    if (!statusPressed.current) setStatus(initialStatus);
  }, [initialStatus]);

  const [conflict, setConflict] = useState<DraftDetail<P> | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Rendered state, unlike the `inFlight` ref below: the ref exists so persist
  // can consult it without re-running effects; this exists so the screen can
  // SHOW "Saving…" — they change together in persist.
  const [saving, setSaving] = useState(false);
  // The save in flight must never race the next one — a stale response writing
  // an old version number back would make every later save a false conflict.
  const inFlight = useRef(false);
  const pendingRef = useRef<{ state: P; customerName: string } | null>(null);

  const persist = useCallback(
    async (snapshot: { state: P; customerName: string }) => {
      if (inFlight.current) {
        pendingRef.current = snapshot;
        return;
      }
      inFlight.current = true;
      setSaving(true);
      try {
        // READ THE REF, NEVER THE CAPTURED `id` — the `finally` below recurses
        // into this same closure, so a captured id is one save out of date.
        const current = idRef.current;
        // `status` is on NEITHER body: both routes are `.strict()` and the
        // autosave must not be able to un-ready a form somebody checked.
        const saved =
          current === null
            ? (
                await api.post<{ data: DraftDetail<P> }>(base, {
                  payload: snapshot.state,
                  customerName: snapshot.customerName,
                })
              ).data
            : (
                await api.put<{ data: DraftDetail<P> }>(one(current), {
                  payload: snapshot.state,
                  customerName: snapshot.customerName,
                  version: versionRef.current,
                })
              ).data;

        // The ref FIRST and unconditionally: a queued snapshot drains inside
        // `finally`, before React has re-rendered, and it must see this id.
        idRef.current = saved.id;
        if (current === null) setId(saved.id);
        versionRef.current = saved.version;
        // `saved.status` is deliberately ignored here — see the note above it.
        setSavedAt(new Date());
      } catch (error) {
        const current = idRef.current;
        if (error instanceof ApiError && error.status === 409 && current !== null) {
          // The other device won. Stop autosaving and fetch its state — writing
          // over it from here is exactly what the version exists to prevent.
          const winning = await api
            .get<{ data: DraftDetail<P> }>(one(current))
            .catch(() => undefined);
          if (winning !== undefined) setConflict(winning.data);
        }
        // Any other failure (offline, 500) is silent: autosave must never
        // interrupt typing, and the next edit retries anyway.
      } finally {
        inFlight.current = false;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending !== null) void persist(pending);
        else setSaving(false);
      }
    },
    // `id` IS NOT A DEPENDENCY ANY MORE — `idRef` carries it, and listing it
    // would rebuild this callback mid-flight for no benefit.
    [base, one],
  );

  useEffect(() => {
    if (!enabled || conflict !== null) return;
    if (!worthKeeping(state)) return;

    const timer = window.setTimeout(() => {
      void persist({ state, customerName });
    }, 2_000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [state, customerName, enabled, conflict, persist]);

  /**
   * The explicit "Save draft" press. The debounce exists so typing does not
   * write on every keystroke — but a person about to walk away should not have
   * to wait two seconds wondering if it caught their last line. Same persist,
   * same versioning; just now.
   */
  const saveNow = useCallback(() => {
    if (!enabled || conflict !== null) return;
    if (!worthKeeping(state)) return;
    void persist({ state, customerName });
  }, [enabled, conflict, state, customerName, persist]);

  const [settingStatus, setSettingStatus] = useState(false);

  /**
   * "I have checked this one" — and its undo.
   *
   * ITS OWN ROUTE, NOT A FIELD ON THE SAVE, and the version is deliberately NOT
   * sent or adopted from the answer. The API does not move `version` here (the
   * long argument is in apps/api/src/routes/document-drafts.ts), so the autosave
   * in flight beside this press keeps the number it already read and does not
   * come back "changed on another device" for something the same person just did
   * on the same screen. Touching `versionRef` from here would recreate exactly
   * that false conflict.
   *
   * Throws on failure rather than swallowing it: unlike the autosave, this is a
   * press, and a press that appears to do nothing is worse than an error.
   */
  const changeStatus = useCallback(
    async (next: DraftStatus) => {
      // Nothing exists to mark yet. The control is disabled in this state; this
      // is the guard that keeps that true if a caller forgets.
      if (id === null) return;
      setSettingStatus(true);
      try {
        const updated = await api.post<{ data: DraftDetail<P> }>(
          endpoints.draftStatus(base, id),
          { status: next },
        );
        // From here on this screen's own answer wins over the loading one: the
        // resumed GET settling late must not undo a press made in front of it.
        statusPressed.current = true;
        setStatus(updated.data.status);
        void queryClient.invalidateQueries({ queryKey: draftsKey(kind) });
      } finally {
        setSettingStatus(false);
      }
    },
    [base, id, kind, queryClient],
  );

  /*
   * THE REF AGAIN, AND HERE IT MATTERS MOST. `completed` below is called from
   * the editor's save handler, which can fire in the same tick as the very
   * first autosave's response — before React has re-rendered with the new id.
   * Reading the `id` STATE there would find `null`, skip the delete, and leave
   * a ghost draft: a numberless second copy of a document that now exists in
   * the register with a number, which is exactly what `completed` exists to
   * prevent.
   */
  const discard = useCallback(async () => {
    const current = idRef.current;
    if (current !== null) {
      await api.delete(one(current)).catch(() => undefined);
      idRef.current = null;
      setId(null);
      void queryClient.invalidateQueries({ queryKey: draftsKey(kind) });
    }
  }, [one, kind, queryClient]);

  /** After the document is saved the draft's job is done — it must not linger as
   * a second copy of a document that now exists with a number. */
  const completed = discard;

  return {
    draftUsedId: id,
    status,
    settingStatus,
    changeStatus,
    savedAt,
    saving,
    conflict,
    // Disabled state for the button: an empty form has nothing worth a row.
    canSaveNow: enabled && conflict === null && worthKeeping(state),
    saveNow,
    discard,
    completed,
  };
}

export function useDeleteDraft(kind: MountedDraftKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(DRAFT_ONE[kind](id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: draftsKey(kind) });
    },
  });
}
